import { format } from 'date-fns'
import { LoadingSpinner } from '@/components/ui/loading'
import { Modal } from '@/components/ui/modal'
import { StatusBadge } from '@/components/ui/status-badge'
import type { EmailDetail } from '@/lib/types'

interface EmailDetailModalProps {
  email: EmailDetail | undefined
  isLoading: boolean
  open: boolean
  onClose: () => void
}

export function EmailDetailModal({
  email,
  isLoading,
  open,
  onClose,
}: EmailDetailModalProps): React.JSX.Element {
  return (
    <Modal open={open} onClose={onClose} title="Email Detail" size="lg">
      {isLoading || !email ? (
        <LoadingSpinner className="py-8" />
      ) : (
        <div className="space-y-6">
          <MetadataSection email={email} />
          <EventTimeline events={email.events} />
        </div>
      )}
    </Modal>
  )
}

function MetadataSection({ email }: { email: EmailDetail }): React.JSX.Element {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-text-primary">Metadata</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <MetadataRow label="Message ID" value={email.message_id} mono />
        <MetadataRow label="Status">
          <StatusBadge status={email.status} />
        </MetadataRow>
        <MetadataRow label="From" value={email.from_address} />
        <MetadataRow label="Transport" value={email.transport} />
        <MetadataRow label="Subject" value={email.subject} />
        <MetadataRow label="Domain" value={email.domain} />
        <MetadataRow label="Account" value={email.account_email} />
        <MetadataRow
          label="Created"
          value={format(new Date(email.created_at), 'MMM d, yyyy HH:mm:ss')}
        />
      </div>

      {/* To addresses */}
      <div>
        <p className="mb-1 text-xs font-medium text-text-secondary">To</p>
        <div className="flex flex-wrap gap-1">
          {email.to_addresses.map((addr) => (
            <span
              key={addr}
              className="rounded-md bg-surface-1 px-2 py-0.5 text-xs text-text-primary"
            >
              {addr}
            </span>
          ))}
        </div>
      </div>

      {/* Tags */}
      {Object.keys(email.tags).length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-text-secondary">Tags</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(email.tags).map(([key, val]) => (
              <span
                key={key}
                className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700"
              >
                {key}: {val}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface MetadataRowProps {
  label: string
  value?: string
  mono?: boolean
  children?: React.ReactNode
}

function MetadataRow({ label, value, mono, children }: MetadataRowProps): React.JSX.Element {
  return (
    <div>
      <p className="text-xs text-text-tertiary">{label}</p>
      {children ?? (
        <p className={`text-text-primary ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '-'}</p>
      )}
    </div>
  )
}

function EventTimeline({ events }: { events: EmailDetail['events'] }): React.JSX.Element {
  if (events.length === 0) {
    return (
      <div>
        <h3 className="text-sm font-medium text-text-primary">Events</h3>
        <p className="mt-2 text-sm text-text-tertiary">No events recorded yet.</p>
      </div>
    )
  }

  const sorted = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  )

  return (
    <div>
      <h3 className="mb-3 text-sm font-medium text-text-primary">Event Timeline</h3>
      <div className="relative space-y-0">
        {/* Vertical line */}
        <div className="absolute left-3 top-2 bottom-2 w-px bg-surface-3" />

        {sorted.map((event) => (
          <div key={event.id} className="relative flex gap-3 pb-4">
            {/* Dot */}
            <EventDot type={event.type} />

            {/* Content */}
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-center gap-2">
                <StatusBadge status={event.type} />
                <span className="text-xs text-text-tertiary">
                  {format(new Date(event.timestamp), 'MMM d, HH:mm:ss')}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-text-secondary">{event.recipient_email}</p>
              {event.bounce_type && (
                <p className="mt-0.5 text-xs font-medium text-red-600">
                  Bounce: {event.bounce_type}
                </p>
              )}
              {event.data && Object.keys(event.data).length > 0 && (
                <SmtpResponseData data={event.data} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function EventDot({ type }: { type: string }): React.JSX.Element {
  const colorMap: Record<string, string> = {
    delivered: 'bg-green-500',
    bounced: 'bg-red-500',
    deferred: 'bg-yellow-500',
    queued: 'bg-gray-400',
    sent: 'bg-blue-500',
    complained: 'bg-orange-500',
  }
  const color = colorMap[type] ?? 'bg-gray-400'

  return (
    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
    </div>
  )
}

function SmtpResponseData({ data }: { data: Record<string, unknown> }): React.JSX.Element | null {
  const smtpResponse = data.smtp_response as string | undefined
  const statusCode = data.status_code as string | undefined
  const enhancedCode = data.enhanced_status_code as string | undefined

  if (!smtpResponse && !statusCode) return null

  return (
    <div className="mt-1 rounded-md bg-surface-1 p-2">
      {statusCode && (
        <p className="text-xs text-text-secondary">
          <span className="font-medium">Code:</span> {statusCode}
          {enhancedCode && ` (${enhancedCode})`}
        </p>
      )}
      {smtpResponse && (
        <p className="mt-0.5 font-mono text-xs text-text-tertiary">{smtpResponse}</p>
      )}
    </div>
  )
}
